import { useState } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
} from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import PropTypes from "prop-types";
import { backendPost } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";
import { useIncomingMailActions } from "../../hooks/useIncomingMailActions.jsx";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";

export default function MailClaimAssignment({ mail }) {
  const [schadenOptions, setSchadenOptions] = useState([]);
  const [selectedSchaden, setSelectedSchaden] = useState(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { assignClaim } = useIncomingMailActions();

  const fetchSchadenOptions = async (searchTerm) => {
    const { data: schaeden } = await backendPost("data/schadens/search", {
      setIsLoading: setLoading,
      enqueueSnackbar,
      toSend: {
        searchTerm: searchTerm,
      },
    });

    return schaeden || [];
  };

  const confirmSchadenSelection = async () => {
    if (!selectedSchaden) return;

    try {
      await assignClaim(mail.id, selectedSchaden.id);
      enqueueSnackbar("Schaden erfolgreich zugeordnet", { variant: "success" });
      setSelectedSchaden(null);
    } catch (error) {
      console.error("Error assigning claim:", error);
    }
  };

  const handleInputChange = async (event, value) => {
    if (value && value.length >= 2) {
      const filteredOptions = await fetchSchadenOptions(value);
      setSchadenOptions(filteredOptions);
    } else {
      setSchadenOptions([]);
    }
  };

  if (mail?.schaden_id) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          Zugeordneter Schaden
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
          {mail.schaden?.schaden_nummer_accredo}
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            window.open("/schaden/" + mail.schaden_id, "_blank");
          }}
          endIcon={<OpenInNew />}
        >
          Schaden Details
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Schaden zuordnen
      </Typography>

      <Autocomplete
        options={schadenOptions}
        getOptionLabel={(option) => option.schaden_nummer_accredo || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedSchaden}
        loading={loading}
        loadingText="Lade Schäden..."
        noOptionsText="Keine Schäden gefunden"
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography variant="body1" fontWeight="bold">
                {option.schaden_nummer_accredo}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                VU: {option.schaden_nummer_vu || "N/A"}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Schaden auswählen"
            variant="outlined"
            size="small"
          />
        )}
        onInputChange={handleInputChange}
        onChange={(event, value) => {
          setSelectedSchaden(value);
        }}
      />

      {selectedSchaden && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Ausgewählter Schaden
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedSchaden.schaden_nummer_accredo}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                window.open("/schaden/" + selectedSchaden.id, "_blank");
              }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Box>

          <Button
            variant="primary"
            size="small"
            onClick={confirmSchadenSelection}
            fullWidth
          >
            Jetzt zuordnen
          </Button>
        </Box>
      )}

      {!selectedSchaden && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, fontSize: "0.875rem" }}
        >
          Wählen Sie einen Schaden aus, um ihn der E-Mail zuzuordnen.
        </Typography>
      )}
    </Box>
  );
}

MailClaimAssignment.propTypes = {
  mail: PropTypes.object,
};
